export type JSONRegisterUser = {
  user: {
    first_name: string;
    last_name: string;
    email: string;
    password: string;
    company?: string;
    phone?: string;
    terms?: {
      shippo?: string;
      ups_technology?: string;
    };
    g_recaptcha_response?: string;
  };
  marketing?: {
    source?: string;
    campaign?: string;
    campaign_term?: string;
    campaign_content?: string;
    medium?: string;
    reg_method?: string;
    attribution_snippet?: string;
    sh_ad1?: string;
    sh_ad2?: string;
    sh_ad3?: string;
    sh_ad4?: string;
    usps_referral_key?: string;
    http_referrer?: string;
    raw_http_referrer?: string;
    raw_query_string?: string;
  };
  user_tracking?: {
    tracker_sessionid?: string;
  };
  context?: {
    lang?: string;
    plan?: string;
    tier?: string;
    cycle?: string;
    next?: string;
    fixed_fee_waived_until?: string;
    feature_test_variant_name?: string;
    pricing_test_variant?: string;
  };
};

type RegisterUser = {
  user: User;
  marketing?: MarketingPayload;
  userTracking?: TrackingPayload;
  context?: {
    locale?: string;
    lang?: string;
    plan?: string;
    tier?: string;
    cycle?: string;
    next?: string;
    fixedFeeWaivedUntil?: string;
    featureTestVariantName?: string;
    pricingTestVariant?: string;
    // Following two are for new onboarding flow
    isOnboarding2024?: boolean;
    monthlyShippingVolume?: string;
  };
};

type User = {
  firstName: string;
  lastName: string;
  companyName?: string;
  email: string;
  password: string;
  phone?: string;
  terms?: {
    shippo?: string;
    upsTechnology?: string;
  };
  gRecaptchaResponse?: string;
};

type MarketingPayload = {
  source?: string;
  campaign?: string;
  campaignTerm?: string;
  campaignContent?: string;
  medium?: string;
  regMethod?: string;
  attributionSnippet?: string;
  shDevice?: string;
  shMatchtype?: string;
  shAd1?: string;
  shAd2?: string;
  shAd3?: string;
  shAd4?: string;
  uspsReferralKey?: string;
  httpReferrer?: string;
  rawHttpReferrer?: string;
  rawQueryString?: string;
};

type TrackingPayload = {
  tracker_sessionid?: string;
};

type RegisterResponse = {
  data: {
    jwt: string;
    session_token: string;
    registration_test: {
      name: string;
      variant: string;
    };
  };
};

function toJSON(registerUser: RegisterUser): JSONRegisterUser {
  let marketing = {};
  if (registerUser.marketing) {
    marketing = {
      source: registerUser.marketing.source,
      campaign: registerUser.marketing.campaign,
      campaign_term: registerUser.marketing.campaignTerm,
      campaign_content: registerUser.marketing.campaignContent,
      medium: registerUser.marketing.medium,
      reg_method: registerUser.marketing.regMethod,
      attribution_snippet: registerUser.marketing.attributionSnippet,
      sh_device: registerUser.marketing.shDevice,
      sh_matchtype: registerUser.marketing.shMatchtype,
      sh_ad1: registerUser.marketing.shAd1,
      sh_ad2: registerUser.marketing.shAd2,
      sh_ad3: registerUser.marketing.shAd3,
      sh_ad4: registerUser.marketing.shAd4,
      usps_referral_key: registerUser.marketing.uspsReferralKey,
      http_referrer: registerUser.marketing.httpReferrer,
      raw_http_referrer: registerUser.marketing.rawHttpReferrer,
      raw_query_string: registerUser.marketing.rawQueryString,
    };
  }

  let userTracking = {};
  if (registerUser.userTracking) {
    userTracking = {
      tracker_sessionid: registerUser.userTracking.tracker_sessionid,
    };
  }

  let context = {};
  if (registerUser.context) {
    context = {
      locale: registerUser.context.locale,
      lang: registerUser.context.lang,
      plan: registerUser.context.plan,
      tier: registerUser.context.tier,
      cycle: registerUser.context.cycle,
      next: registerUser.context.next,
      fixed_fee_waived_until: registerUser.context.fixedFeeWaivedUntil,
      feature_test_variant_name: registerUser.context.featureTestVariantName,
      pricing_test_variant: registerUser.context.pricingTestVariant,
    };
  }

  return {
    user: {
      first_name: registerUser.user.firstName,
      last_name: registerUser.user.lastName,
      company: registerUser.user.companyName,
      email: registerUser.user.email,
      password: registerUser.user.password,
      phone: registerUser.user.phone,
      terms: {
        shippo: registerUser.user.terms?.shippo,
        ups_technology: registerUser.user.terms?.upsTechnology,
      },
      g_recaptcha_response: registerUser.user.gRecaptchaResponse,
    },
    marketing: { ...marketing },
    context: { ...context },
    user_tracking: { ...userTracking },
  };
}

export default RegisterUser;

export { RegisterUser, toJSON, RegisterResponse, MarketingPayload, User, TrackingPayload };
